import { AfterViewInit, Directive, ElementRef, inject, Input, Renderer2 } from '@angular/core';

/**
 * Contrast color.
 * Change color of text based on background color.
 *
 * @example
 * ```html
 * <div bgContrast [style.background-color]="'rgb(0, 0, 0)'"></div>
 *
 * // or
 *
 * <div bgContrast="#000000"></div>
 * ```
 */
@Directive({
    selector: '[bgContrast]',
    standalone: true
})
export class BgContrastDirective implements AfterViewInit {
    @Input({
        required: true
    })
    bgContrast!: string;

    @Input()
    tolerance = 175;

    protected elementRef = inject(ElementRef);
    protected renderer = inject(Renderer2);

    ngAfterViewInit(): void {
        let backgroundColor = this.elementRef.nativeElement.style.backgroundColor;

        if (this.bgContrast) {
            backgroundColor = this.bgContrast[0] === '#' ? hexToRgb(this.bgContrast) : this.bgContrast;
        }

        if (backgroundColor) {
            let rgb: number[];

            if (backgroundColor.indexOf('rgba') === 0) {
                rgb = backgroundColor.match(/^rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(-?\d+|-?\d*.\d+)\s*\)$/i);
            } else if (backgroundColor.indexOf('rgb') === 0 && backgroundColor.match(/,/g)?.length === 3) {
                // case when we have rgba but with rgb name
                rgb = backgroundColor.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(-?\d+|-?\d*.\d+)\s*\)$/i);
            } else {
                rgb = backgroundColor.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
            }

            if (rgb && (rgb.length === 4 || rgb.length === 5)) {
                const yiq = (rgb[1] * 299 + rgb[2] * 587 + rgb[3] * 114) / 1000;

                if (yiq < this.tolerance) {
                    this.elementRef.nativeElement.style.color = `white`;
                }

                if (yiq >= this.tolerance) {
                    this.renderer.addClass(this.elementRef.nativeElement, 'is-light-bg');
                } else {
                    this.renderer.removeClass(this.elementRef.nativeElement, 'is-dark-bg');
                }
            }
        }
    }
}

/**
 * Parse HEX to RGB.
 *
 * @param hex {string}
 */
export const hexToRgb = (hex: string) => {
    if (!hex) {
        return { r: 255, g: 255, b: 255 };
    }

    hex = hex.replace('#', '');

    const isShort = hex.length === 3 || hex.length === 4;

    const r = parseInt(isShort ? `${hex.slice(0, 1)}${hex.slice(0, 1)}` : hex.slice(0, 2), 16);
    const g = parseInt(isShort ? `${hex.slice(1, 2)}${hex.slice(1, 2)}` : hex.slice(2, 4), 16);
    const b = parseInt(isShort ? `${hex.slice(2, 3)}${hex.slice(2, 3)}` : hex.slice(4, 6), 16);

    return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Parse RGB to HEX.
 *
 * @param rgb {string}
 */
export const rgbToHex = (rgb: string) => {
    if (!rgb) {
        return '#FFFFFF';
    }

    const rgbValues = rgb.replace(/[^\d,]/g, '').split(',');

    const r = parseInt(rgbValues[0], 10);
    const g = parseInt(rgbValues[1], 10);
    const b = parseInt(rgbValues[2], 10);

    const hex = ((r << 16) | (g << 8) | b).toString(16);

    return `#${hex.padStart(6, '0')}`;
};
